import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  Grid,
  Switch
} from '@mui/material';
import {
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  CurrencyExchange as CurrencyExchangeIcon
} from '@mui/icons-material';
import api from '@/services/authService';

interface Currency {
  id: number;
  code: string;
  name: string;
  symbol: string;
  isActive: boolean;
}

interface ExchangeRate {
  id: number;
  fromCurrency: string;
  toCurrency: string;
  rate: number;
  isActive: boolean;
}

const CurrencyManagement: React.FC = () => {
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [exchangeRates, setExchangeRates] = useState<ExchangeRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<ExchangeRate | null>(null);
  const [newRate, setNewRate] = useState<number>(0);

  useEffect(() => {
    fetchCurrencies();
    fetchExchangeRates();
  }, []);

  const fetchCurrencies = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await api.get('/currency', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data?.success) {
        setCurrencies(response.data.data?.currencies || []);
      } else {
        setCurrencies([]);
      }
    } catch (err) {
      setError('Failed to fetch currencies');
    }
  };

  const fetchExchangeRates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await api.get('/currency/exchange-rates', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data?.success) {
        setExchangeRates(response.data.data?.exchangeRates || []);
      } else {
        setExchangeRates([]);
      }
    } catch (err) {
      setError('Failed to fetch exchange rates');
    } finally {
      setLoading(false);
    }
  };

  const handleEditRate = (rate: ExchangeRate) => {
    setEditingRate(rate);
    setNewRate(rate.rate);
    setEditDialogOpen(true);
  };

  const handleToggleCurrencyStatus = async (currencyCode: string, currentStatus: boolean) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await api.put(
        `/currency/${currencyCode}`,
        { isActive: !currentStatus },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccess(`Currency ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
        fetchCurrencies();
      } else {
        setError('Failed to update currency status');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const handleSaveRate = async () => {
    if (!editingRate) return;
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await api.put(
        '/currency/exchange-rates',
        { rates: [{ ...editingRate, rate: String(newRate) }] },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccess('Exchange rate updated successfully');
        fetchExchangeRates();
        setEditDialogOpen(false);
      } else {
        setError('Failed to update exchange rate');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const getCurrencyName = (code: string) => {
    const currency = currencies.find(c => c.code === code);
    return currency ? `${currency.name} (${currency.symbol})` : code;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight="600">
          Currency Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => { fetchCurrencies(); fetchExchangeRates(); }}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Currencies Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CurrencyExchangeIcon sx={{ mr: 1, color: '#6366f1' }} />
                <Typography variant="h6" fontWeight="600">
                  Supported Currencies
                </Typography>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Code</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Symbol</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currencies.map((currency) => (
                      <TableRow key={currency.id}>
                        <TableCell sx={{ fontWeight: 600 }}>{currency.code}</TableCell>
                        <TableCell>{currency.name}</TableCell>
                        <TableCell>{currency.symbol}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Switch
                              checked={currency.isActive}
                              onChange={() => handleToggleCurrencyStatus(currency.code, currency.isActive)}
                              color="success"
                              size="small"
                            />
                            <Chip
                              label={currency.isActive ? 'Active' : 'Inactive'}
                              color={currency.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Exchange Rates Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CurrencyExchangeIcon sx={{ mr: 1, color: '#6366f1' }} />
                <Typography variant="h6" fontWeight="600">
                  Exchange Rates
                </Typography>
              </Box>
              {loading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="textSecondary">Loading exchange rates...</Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>From</TableCell>
                        <TableCell>To</TableCell>
                        <TableCell>Rate</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {exchangeRates.map((rate) => (
                        <TableRow key={rate.id}>
                          <TableCell sx={{ fontWeight: 600 }}>{rate.fromCurrency}</TableCell>
                          <TableCell>{rate.toCurrency}</TableCell>
                          <TableCell>
                            {typeof rate.rate === 'number'
                              ? rate.rate.toFixed(6)
                              : parseFloat(rate.rate).toFixed(6)}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit Rate">
                              <IconButton
                                size="small"
                                onClick={() => handleEditRate(rate)}
                                sx={{ color: '#6366f1' }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                      {exchangeRates.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography color="textSecondary">No exchange rates found</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Exchange Rate Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Exchange Rate</DialogTitle>
        <DialogContent>
          {editingRate && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {getCurrencyName(editingRate.fromCurrency)} → {getCurrencyName(editingRate.toCurrency)}
              </Typography>
              <TextField
                fullWidth
                label="Exchange Rate"
                type="number"
                value={newRate}
                onChange={(e) => setNewRate(parseFloat(e.target.value) || 0)}
                inputProps={{ step: 0.000001, min: 0 }}
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveRate}
            disabled={!newRate || newRate <= 0}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CurrencyManagement;
